/**
 * React Query хуки для административных операций (AdminOffice)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from '../services/toast';
import {
  fetchClients,
  fetchDeals,
  fetchClientData,
  fetchReviveApplication,
  updateApplicationPointOfSaleAdm,
  fetchDealDocumentsByContractId,
  fetchSetDealIssued,
  fetchRevokeAccount,
  fetchRevokeAccountApplication,
  fetchRejectCrifApplication,
  fetchDeactivate9FZApplication,
  sendDocumentsToIbso,
  fetchLastAllRepaymentDate,
  fetchPayOffDebts,
  fetchPayment,
  fetchDirectReceipt,
  fetchDirectReceipts,
  fetchUploadManualPayments,
  fetchUpdateClientData,
  fetchUploadAverageIncome,
  fetchUploadFullCost,
  fetchUploadPointOfSale,
  fetchUploadPskMapping,
  fetchUpload9FZRegistry,
  fetchCreateNewUserManager,
  fetchDealRegistryPack,
  fetchCurrentMpl80State,
  changeCurrentMpl80State,
  fetchCurrentMpl5080State,
  changeCurrentMpl5080State,
  fetchCurrentPaymentState,
  changeCurrentPaymentState,
  fetchCurrentRateFilterState,
  changeCurrentRateFilterState,
  getAdminDocumentTypes,
  generateDocumentRequest,
  fetchRevokeAccounts,
} from '../api/AdminApi';
import type {
  AdminClient,
  Deal,
  ClientData,
  ClientSearchParams,
  PaymentParams,
  DirectReceiptParams,
  DocumentType,
  RateFilter,
  DocumentGenerationParams,
  ClientDataResponse,
  FileUploadResponse,
} from '../types';
import { handleApiError } from '../utils/errorHandlers';

// ============================================
// QUERIES
// ============================================

/**
 * Поиск клиентов по параметрам
 */
export const useClients = (searchParams: ClientSearchParams, enabled = false) => {
  return useQuery<AdminClient[]>({
    queryKey: ['admin', 'clients', searchParams],
    queryFn: () => fetchClients(searchParams),
    enabled: enabled && Object.values(searchParams).some((v) => v !== null && v !== ''),
    staleTime: 5 * 60 * 1000, // 5 минут
  });
};

/**
 * Получение сделок клиента
 */
export const useDeals = (clientId: number | null) => {
  return useQuery<Deal[]>({
    queryKey: ['admin', 'deals', clientId],
    queryFn: () => fetchDeals(clientId!),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Получение данных клиента
 */
export const useClientData = (clientId: number | null) => {
  return useQuery<ClientData>({
    queryKey: ['admin', 'clientData', clientId],
    queryFn: () => fetchClientData(clientId!),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Дата последнего массового погашения
 */
export const useLastRepaymentDate = () => {
  return useQuery<string>({
    queryKey: ['admin', 'lastRepaymentDate'],
    queryFn: fetchLastAllRepaymentDate,
    staleTime: 1 * 60 * 1000, // 1 минута
  });
};

/**
 * Состояние динамического свойства МПЛ 80
 */
export const useMpl80State = () => {
  return useQuery<boolean>({
    queryKey: ['admin', 'mpl80State'],
    queryFn: fetchCurrentMpl80State,
    staleTime: 30 * 1000, // 30 секунд
  });
};

/**
 * Состояние динамического свойства МПЛ 50-80
 */
export const useMpl5080State = () => {
  return useQuery<boolean>({
    queryKey: ['admin', 'mpl5080State'],
    queryFn: fetchCurrentMpl5080State,
    staleTime: 30 * 1000,
  });
};

/**
 * Состояние автоматической отправки платежей
 */
export const usePaymentState = () => {
  return useQuery<boolean>({
    queryKey: ['admin', 'paymentState'],
    queryFn: fetchCurrentPaymentState,
    staleTime: 30 * 1000,
  });
};

/**
 * Фильтры заявок по ставкам
 */
export const useRateFilters = () => {
  return useQuery<RateFilter[]>({
    queryKey: ['admin', 'rateFilters'],
    queryFn: fetchCurrentRateFilterState,
    staleTime: 1 * 60 * 1000,
  });
};

/**
 * Типы документов для генерации
 */
export const useDocumentTypes = () => {
  return useQuery<DocumentType[]>({
    queryKey: ['admin', 'documentTypes'],
    queryFn: getAdminDocumentTypes,
    staleTime: 60 * 60 * 1000, // 1 час
  });
};

// ============================================
// MUTATIONS - ВЫДАЧА/ЗАКРЫТИЕ
// ============================================

/**
 * Реанимация заявки
 */
export const useReviveApplication = () => {
  return useMutation({
    mutationFn: (applicationId: number) => fetchReviveApplication(applicationId),
    onSuccess: (_, applicationId) => {
      toast.success(`Заявка ${applicationId} успешно реанимирована`);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Смена точки продаж для заявки
 */
export const useUpdatePointOfSale = () => {
  return useMutation({
    mutationFn: ({ creditContractId, pointOfSaleId }: { creditContractId: number; pointOfSaleId: number }) =>
      updateApplicationPointOfSaleAdm(creditContractId, pointOfSaleId),
    onSuccess: () => {
      toast.success('Точка продаж успешно обновлена');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Скачивание документов сделки
 */
export const useDownloadDealDocuments = () => {
  return useMutation({
    mutationFn: (creditContractId: number) => fetchDealDocumentsByContractId(creditContractId),
    onSuccess: () => {
      toast.success('Документы успешно скачаны');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Установка статуса "Выдан"
 */
export const useSetDealIssued = () => {
  return useMutation({
    mutationFn: (creditContractId: number) => fetchSetDealIssued(creditContractId),
    onSuccess: () => {
      toast.success('Сделка установлена в статус "Выдан"');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Снятие авторизации
 */
export const useRevokeAccount = () => {
  return useMutation({
    mutationFn: (creditContractId: number) => fetchRevokeAccount(creditContractId),
    onSuccess: () => {
      toast.success('Запрос на снятие авторизации отправлен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Снятие авторизации по заявке
 */
export const useRevokeAccountApplication = () => {
  return useMutation({
    mutationFn: (applicationId: number) => fetchRevokeAccountApplication(applicationId),
    onSuccess: () => {
      toast.success('Запрос на снятие авторизации отправлен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Загрузка файла со снятием авторизации по заявкам
 */
export const useRevokeAccounts = () => {
  return useMutation({
    mutationFn: (formData: FormData) => fetchRevokeAccounts(formData),
    onSuccess: (response: string) => {
      toast.success(response || 'Файл с заявками на снятие авторизации успешно принят в работу');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Отказ в CRIF
 */
export const useRejectCrif = () => {
  return useMutation({
    mutationFn: (applicationId: number) => fetchRejectCrifApplication(applicationId),
    onSuccess: (_, applicationId) => {
      toast.success(`Отказ в CRIF по заявке ${applicationId} отправлен`);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Деактивация по 9-ФЗ
 */
export const useDeactivate9FZ = () => {
  return useMutation({
    mutationFn: (applicationId: number) => fetchDeactivate9FZApplication(applicationId),
    onSuccess: (_, applicationId) => {
      toast.success(`Заявка ${applicationId} деактивирована по 9-ФЗ`);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Отправка документов в ИБСО
 */
export const useSendDocumentsToIbso = () => {
  return useMutation({
    mutationFn: (applicationId: number) => sendDocumentsToIbso(applicationId),
    onSuccess: (data) => {
      toast.success(data || 'Документы отправлены в ИБСО');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

// ============================================
// MUTATIONS - ПОГАШЕНИЯ
// ============================================

/**
 * Массовое погашение всех сделок
 */
export const usePayOffDebts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: fetchPayOffDebts,
    onSuccess: (errorData) => {
      if (!errorData) {
        toast.success('Запрос на погашение всех сделок отправлен');
        queryClient.invalidateQueries({ queryKey: ['admin', 'lastRepaymentDate'] });
      } else {
        toast.error(`Ошибка: ${errorData}`);
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Точечное погашение по клиенту
 */
export const useCreatePayment = () => {
  return useMutation({
    mutationFn: (params: PaymentParams) => fetchPayment(params),
    onSuccess: () => {
      toast.success('Запрос на погашение отправлен в ИБСО');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Погашение через р/с МКК
 */
export const useCreateDirectReceipt = () => {
  return useMutation({
    mutationFn: (params: DirectReceiptParams) => fetchDirectReceipt(params),
    onSuccess: () => {
      toast.success('Погашение на р/с МКК успешно добавлено');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Загрузка файла с погашениями
 */
export const useUploadPayments = () => {
  return useMutation({
    mutationFn: (formData: FormData) => fetchUploadManualPayments(formData),
    onSuccess: () => {
      toast.success('Файл с погашениями успешно загружен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Загрузка файла с погашениями через р/с
 */
export const useUploadDirectReceipts = () => {
  return useMutation({
    mutationFn: (formData: FormData) => fetchDirectReceipts(formData),
    onSuccess: (response: string) => {
      toast.success(response || 'Файл с погашениями через р/с успешно загружен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

// ============================================
// MUTATIONS - РЕДАКТИРОВАНИЕ КЛИЕНТА
// ============================================

/**
 * Обновление данных клиента
 */
export const useUpdateClientData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientData: ClientData) => fetchUpdateClientData(clientData),
    onSuccess: (_, variables) => {
      toast.success('Данные клиента успешно обновлены');
      queryClient.invalidateQueries({ queryKey: ['admin', 'clientData', variables.id] });
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

// ============================================
// MUTATIONS - СПРАВОЧНИКИ
// ============================================

/**
 * Загрузка файла со среднедушевым доходом
 */
export const useUploadAverageIncome = () => {
  return useMutation({
    mutationFn: (formData: FormData) => fetchUploadAverageIncome(formData),
    onSuccess: () => {
      toast.success('Файл со среднедушевым доходом успешно загружен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Загрузка файла с ПСК
 */
export const useUploadFullCost = () => {
  return useMutation({
    mutationFn: (formData: FormData) => fetchUploadFullCost(formData),
    onSuccess: () => {
      toast.success('Файл с ПСК успешно загружен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Загрузка файла с точками продаж
 */
export const useUploadPointsOfSale = () => {
  return useMutation({
    mutationFn: (formData: FormData) => fetchUploadPointOfSale(formData),
    onSuccess: () => {
      toast.success('Файл с точками продаж успешно загружен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Загрузка файла с меппингом ПСК
 */
export const useUploadPskMapping = () => {
  return useMutation({
    mutationFn: (formData: FormData) => fetchUploadPskMapping(formData),
    onSuccess: () => {
      toast.success('Файл меппинга ПСК успешно загружен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Загрузка реестра 9-ФЗ
 */
export const useUpload9FZRegistry = () => {
  return useMutation({
    mutationFn: (formData: FormData) => fetchUpload9FZRegistry(formData),
    onSuccess: () => {
      toast.success('Реестр ЦБ по 9-ФЗ успешно загружен');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

// ============================================
// MUTATIONS - ПОЛЬЗОВАТЕЛИ
// ============================================

/**
 * Создание пользователя с ролью "Менеджер"
 */
export const useCreateUserManager = () => {
  return useMutation({
    mutationFn: ({ login, password, comment }: { login: string; password: string; comment: string }) =>
      fetchCreateNewUserManager(login, password, comment),
    onSuccess: (_, variables) => {
      toast.success(`Пользователь ${variables.login} успешно создан`);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

// ============================================
// MUTATIONS - ПРОЧЕЕ
// ============================================

/**
 * Скачивание архива реестров ИБСО
 */
export const useDownloadRegistryPack = () => {
  return useMutation({
    mutationFn: (creditContractId: number) => fetchDealRegistryPack(creditContractId),
    onSuccess: () => {
      toast.success('Архив реестров успешно скачан');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Переключение динамического свойства МПЛ 80
 */
export const useToggleMpl80 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changeCurrentMpl80State,
    onSuccess: (newState) => {
      toast.success(`Проверка МПЛ 80: ${newState ? 'Включена' : 'Выключена'}`);
      queryClient.setQueryData(['admin', 'mpl80State'], newState);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Переключение динамического свойства МПЛ 50-80
 */
export const useToggleMpl5080 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changeCurrentMpl5080State,
    onSuccess: (newState) => {
      toast.success(`Проверка МПЛ 50-80: ${newState ? 'Включена' : 'Выключена'}`);
      queryClient.setQueryData(['admin', 'mpl5080State'], newState);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Переключение автоматической отправки платежей
 */
export const useTogglePaymentState = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changeCurrentPaymentState,
    onSuccess: (newState) => {
      toast.success(`Автоматическая отправка платежей: ${newState ? 'Включена' : 'Выключена'}`);
      queryClient.setQueryData(['admin', 'paymentState'], newState);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Переключение фильтра заявок по ставкам
 */
export const useToggleRateFilter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rateFilterId: string) => changeCurrentRateFilterState(rateFilterId),
    onSuccess: (updatedFilter) => {
      if (updatedFilter) {
        toast.success(`Фильтр "${updatedFilter.description}" обновлен`);
        queryClient.invalidateQueries({ queryKey: ['admin', 'rateFilters'] });
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};

/**
 * Генерация документов
 */
export const useGenerateDocument = () => {
  return useMutation({
    mutationFn: (params: DocumentGenerationParams) => generateDocumentRequest(params),
    onSuccess: (success) => {
      if (success) {
        toast.success('Документ успешно сгенерирован');
      } else {
        toast.error('Ошибка при генерации документа');
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });
};
